import React, { useRef } from "react";
import { WorkCharge } from "../../types/garage/atelier";
import { JOB_STATUSES, useAtelierStore } from "@store/atelierStore";

// --- Status Legend ---

export function StatusLegend() {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <span
        className="text-h4 font-medium"
        style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
      >
        Statuts
      </span>
      {JOB_STATUSES.map((s) => (
        <div
          key={s.id}
          className="flex flex-row items-center"
          style={{ gap: 8 }}
        >
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 3,
              background: s.bgColor,
              border: `1px solid ${s.borderColor}`,
            }}
          />
          <span
            className="text-h4 font-medium"
            style={{ fontFamily: "Poppins", color: "#737373" }}
          >
            {s.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Charge Card ---

export function ChargeCard({
  charge,
  onSplitClick,
  onDragStart,
}: {
  charge: WorkCharge;
  onSplitClick: (c: WorkCharge) => void;
  onDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);
  const setHoveredChargeId = useAtelierStore((s) => s.setHoveredChargeId);

  return (
    <div
      ref={cardRef}
      className="flex flex-row items-center justify-between shadow-lg"
      style={{
        background: "#FFF",
        borderRadius: 6,
        padding: "6px 8px",
        cursor: "grab",
        userSelect: "none",
        touchAction: "none",
      }}
      onMouseEnter={() => setHoveredChargeId(charge.id)}
      onMouseLeave={() => setHoveredChargeId(null)}
      onPointerDown={(e) => {
        pointerStartRef.current = { x: e.clientX, y: e.clientY };
        isDragging.current = false;
      }}
      onPointerMove={(e) => {
        if (!pointerStartRef.current) return;
        if (
          Math.abs(e.clientX - pointerStartRef.current.x) > 4 ||
          Math.abs(e.clientY - pointerStartRef.current.y) > 4
        ) {
          if (!isDragging.current) {
            isDragging.current = true;
            // Capture pointer on the card div itself (not e.target which may be a child span)
            if (cardRef.current) {
              cardRef.current.setPointerCapture(e.pointerId);
            }
            onDragStart(e, charge);
          }
        }
      }}
      onPointerUp={(e) => {
        if (isDragging.current && cardRef.current) {
          cardRef.current.releasePointerCapture(e.pointerId);
        }
        if (!isDragging.current && pointerStartRef.current)
          onSplitClick(charge);
        pointerStartRef.current = null;
        isDragging.current = false;
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{
          fontFamily: "Poppins",
          color: "#525252",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {charge.clientName}
      </span>
      <span
        className="text-h4 font-semibold"
        style={{ fontFamily: "Poppins", color: "#A1A1A1", marginLeft: 4 }}
      >
        ({charge.hours}h)
      </span>
    </div>
  );
}

// --- Charge Section ---

export function ChargeSection({
  title,
  status,
  charges,
  onSplitClick,
  onChargeDragStart,
  isDropTarget,
}: {
  title: string;
  status: WorkCharge["status"];
  charges: WorkCharge[];
  onSplitClick: (c: WorkCharge) => void;
  onChargeDragStart: (e: React.PointerEvent, c: WorkCharge) => void;
  isDropTarget?: boolean;
}) {
  return (
    <div
      data-charge-section={status}
      className="flex flex-col"
      style={{
        gap: 6,
        padding: isDropTarget ? "8px 6px" : undefined,
        border: isDropTarget ? "2px dashed #6366F1" : "2px dashed transparent",
        borderRadius: 8,
        background: isDropTarget ? "#EEF0FF" : undefined,
        transition: "all 0.15s",
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{ fontFamily: "Poppins", color: "#A1A1A1" }}
      >
        {title}
      </span>
      {charges.map((c) => (
        <ChargeCard
          key={c.id}
          charge={c}
          onSplitClick={onSplitClick}
          onDragStart={onChargeDragStart}
        />
      ))}
      {charges.length === 0 && (
        <span
          className="text-h4 font-medium"
          style={{
            fontFamily: "Poppins",
            color: "#D9D9D9",
            fontStyle: "italic",
          }}
        >
          Aucune charge
        </span>
      )}
    </div>
  );
}

// --- Drop zone indicator for returning jobs to charges ---

export function ChargeDropZone({ active }: { active: boolean }) {
  return (
    <div
      className="flex items-center justify-center"
      style={{
        border: `2px dashed ${active ? "#6366F1" : "#D9D9D9"}`,
        borderRadius: 8,
        padding: "12px 8px",
        background: active ? "#EEF0FF" : "#FAFAFA",
        transition: "all 0.15s",
        marginTop: 8,
      }}
    >
      <span
        className="text-h4 font-medium"
        style={{
          fontFamily: "Poppins",
          color: active ? "#6366F1" : "#A1A1A1",
          textAlign: "center",
        }}
      >
        {active
          ? "Relâcher pour renvoyer en charge"
          : "Glisser un RDV ici pour le retirer"}
      </span>
    </div>
  );
}
